import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthFetch } from "../hooks/useAuthFetch";
import "../stili/MovimentiCassa.css";


const API_URL = process.env.REACT_APP_API_URL;

const TIPI_MOVIMENTO = [
  { value: "ENTRATA", label: "Entrata" },
  { value: "USCITA", label: "Uscita" },
];

const METODI_PAGAMENTO = [
  { value: "CONTANTI", label: "Contanti" },
  { value: "BONIFICO", label: "Bonifico" },
  { value: "POS", label: "POS" },
  { value: "ASSEGNO", label: "Assegno" },
  { value: "ALTRO", label: "Altro" },
];

const CATEGORIE_ENTRATA = [
  { value: "QUOTE", label: "Quote" },
  { value: "SPONSOR", label: "Sponsor" },
  { value: "CONTRIBUTI", label: "Contributi" },
  { value: "EVENTI", label: "Eventi" },
  { value: "RIMBORSI", label: "Rimborsi" },
  { value: "MATERIALE_SPORTIVO", label: "Materiale sportivo" },
  { value: "ALTRE_ENTRATE", label: "Altre entrate" },
];

const CATEGORIE_USCITA = [
  { value: "AFFITTO_CAMPI", label: "Affitto campi" },
  { value: "MATERIALE_SPORTIVO", label: "Materiale sportivo" },
  { value: "ARBITRI", label: "Arbitri" },
  { value: "RIMBORSI_STAFF", label: "Rimborsi staff" },
  { value: "VISITE_MEDICHE", label: "Visite mediche" },
  { value: "TASSE", label: "Tasse" },
  { value: "FEDERAZIONE", label: "Federazione" },
  { value: "MANUTENZIONE", label: "Manutenzione" },
  { value: "UTENZE", label: "Utenze" },
  { value: "ALTRE_USCITE", label: "Altre uscite" },
];

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 12,
    background: "rgba(255,255,255,0.10)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    borderColor: state.isFocused
      ? "rgba(255,255,255,0.40)"
      : "rgba(255,255,255,0.18)",
    boxShadow: "none",
    cursor: "pointer",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    overflow: "hidden",
    background: "rgba(15, 23, 42, 0.96)",
    border: "1px solid rgba(255,255,255,0.16)",
    zIndex: 20,
  }),
  singleValue: (base) => ({
    ...base,
    color: "#fff",
  }),
  input: (base) => ({
    ...base,
    color: "#fff",
  }),
  placeholder: (base) => ({
    ...base,
    color: "rgba(255,255,255,0.75)",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? "rgba(255,255,255,0.12)"
      : "transparent",
    color: "#fff",
    cursor: "pointer",
  }),
};

function getToday() {
  return new Date().toISOString().slice(0, 10);
}

function formatCurrency(value) {
  const num = Number(value || 0);
  return num.toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
  });
}

export default function MovimentiCassaForm() {
  const navigate = useNavigate();
  const authFetch = useAuthFetch();

  const [stagioni, setStagioni] = useState([]);
  const [loadingLookup, setLoadingLookup] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    idStagione: "",
    dataMovimento: getToday(),
    tipoMovimento: "",
    categoria: "",
    descrizione: "",
    importo: "",
    metodoPagamento: "",
    riferimento: "",
    note: "",
  });

  const categorieDisponibili = useMemo(() => {
    if (form.tipoMovimento === "ENTRATA") return CATEGORIE_ENTRATA;
    if (form.tipoMovimento === "USCITA") return CATEGORIE_USCITA;
    return [];
  }, [form.tipoMovimento]);

  useEffect(() => {
    fetchStagioni();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchStagioni() {
    setLoadingLookup(true);
    try {
      const res = await authFetch(`${API_URL}/api/admin/lookup/stagioni`);
      if (!res.ok) {
        throw new Error("Errore caricamento stagioni");
      }

      const data = await res.json();
      const options = (Array.isArray(data) ? data : []).map((s) => ({
        value: String(s.id),
        label: s.nome || s.descrizione || `Stagione ${s.id}`,
      }));
      setStagioni(options);
    } catch (err) {
      toast.error(err.message || "Errore durante il caricamento delle stagioni");
    } finally {
      setLoadingLookup(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function handleTipoChange(selected) {
    setForm((prev) => ({
      ...prev,
      tipoMovimento: selected?.value || "",
      categoria: "",
    }));
  }

  function handleSelectChange(name, selected) {
    setForm((prev) => ({
      ...prev,
      [name]: selected?.value || "",
    }));
  }

  function validateForm() {
    if (!form.idStagione) {
      toast.warning("Seleziona la stagione");
      return false;
    }
    if (!form.dataMovimento) {
      toast.warning("Inserisci la data movimento");
      return false;
    }
    if (!form.tipoMovimento) {
      toast.warning("Seleziona il tipo movimento");
      return false;
    }
    if (!form.categoria) {
      toast.warning("Seleziona la categoria");
      return false;
    }
    if (!form.descrizione.trim()) {
      toast.warning("Inserisci la descrizione");
      return false;
    }
    if (!form.importo || Number(form.importo) <= 0) {
      toast.warning("Inserisci un importo valido");
      return false;
    }
    return true;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      idStagione: Number(form.idStagione),
      dataMovimento: form.dataMovimento,
      tipoMovimento: form.tipoMovimento,
      categoria: form.categoria,
      descrizione: form.descrizione.trim(),
      importo: Number(form.importo),
      metodoPagamento: form.metodoPagamento || null,
      riferimento: form.riferimento.trim() || null,
      note: form.note.trim() || null,
    };

    setSaving(true);
    try {
      const res = await authFetch(`${API_URL}/api/admin/movimenti-cassa`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Errore durante il salvataggio del movimento");
      }

      await res.json().catch(() => null);

      toast.success("Movimento registrato con successo");

      setForm({
        idStagione: form.idStagione,
        dataMovimento: getToday(),
        tipoMovimento: "",
        categoria: "",
        descrizione: "",
        importo: "",
        metodoPagamento: "",
        riferimento: "",
        note: "",
      });
    } catch (err) {
      toast.error(err.message || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  }

  const stagioneSelected =
    stagioni.find((s) => s.value === String(form.idStagione)) || null;
  const tipoSelected =
    TIPI_MOVIMENTO.find((t) => t.value === form.tipoMovimento) || null;
  const categoriaSelected =
    categorieDisponibili.find((c) => c.value === form.categoria) || null;
  const metodoSelected =
    METODI_PAGAMENTO.find((m) => m.value === form.metodoPagamento) || null;

  return (
    <div className="sfondo-immagine">
      <form className="movimenti-cassa-container" onSubmit={handleSubmit}>
        <h2 className="movimenti-cassa-title">Registra Movimento di Cassa</h2>

        <div className="movimenti-cassa-grid due-colonne">
          <div>
            <label>Stagione</label>
            <Select
              options={stagioni}
              value={stagioneSelected}
              onChange={(selected) => handleSelectChange("idStagione", selected)}
              placeholder={loadingLookup ? "Caricamento..." : "Seleziona stagione"}
              isClearable
              isDisabled={loadingLookup}
              styles={customSelectStyles}
            />
          </div>

          <div>
            <label>Data movimento</label>
            <input
              type="date"
              name="dataMovimento"
              value={form.dataMovimento}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="movimenti-cassa-grid due-colonne">
          <div>
            <label>Tipo movimento</label>
            <Select
              options={TIPI_MOVIMENTO}
              value={tipoSelected}
              onChange={handleTipoChange}
              placeholder="Seleziona tipo"
              isClearable
              styles={customSelectStyles}
            />
          </div>

          <div>
            <label>Categoria</label>
            <Select
              options={categorieDisponibili}
              value={categoriaSelected}
              onChange={(selected) => handleSelectChange("categoria", selected)}
              placeholder={
                form.tipoMovimento
                  ? "Seleziona categoria"
                  : "Prima seleziona il tipo movimento"
              }
              isClearable
              isDisabled={!form.tipoMovimento}
              styles={customSelectStyles}
            />
          </div>
        </div>

        <div className="movimenti-cassa-grid due-colonne">
          <div>
            <label>Importo</label>
            <input
              type="number"
              name="importo"
              value={form.importo}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="Es. 150.00"
              required
            />
          </div>

          <div>
            <label>Metodo pagamento</label>
            <Select
              options={METODI_PAGAMENTO}
              value={metodoSelected}
              onChange={(selected) =>
                handleSelectChange("metodoPagamento", selected)
              }
              placeholder="Seleziona metodo"
              isClearable
              styles={customSelectStyles}
            />
          </div>
        </div>

        <div className="movimenti-cassa-grid una-colonna">
          <div>
            <label>Descrizione</label>
            <input
              type="text"
              name="descrizione"
              value={form.descrizione}
              onChange={handleChange}
              maxLength={255}
              placeholder="Es. Pagamento quota Rossi Mario / Acquisto materiale sportivo"
              required
            />
          </div>
        </div>

        <div className="movimenti-cassa-grid due-colonne">
          <div>
            <label>Riferimento</label>
            <input
              type="text"
              name="riferimento"
              value={form.riferimento}
              onChange={handleChange}
              maxLength={100}
              placeholder="Es. ricevuta, fattura, bonifico..."
            />
          </div>

          <div>
            <label>Anteprima importo</label>
            <div className="movimento-cassa-preview">
              {formatCurrency(form.importo)}
            </div>
          </div>
        </div>

        <div className="movimenti-cassa-grid una-colonna">
          <div>
            <label>Note</label>
            <textarea
              name="note"
              value={form.note}
              onChange={handleChange}
              rows={4}
              maxLength={500}
              placeholder="Note facoltative"
            />
          </div>
        </div>

        <div className="bottoni-finali">
          <button
            type="button"
            className="btn-secondario"
            onClick={() => navigate("/gestionale")}
          >
            Torna al gestionale
          </button>

          <button
            type="button"
            className="btn-secondario"
            onClick={() => navigate("/gestionale/admin/movimenti-cassa/aggiorna")}
          >
            Vai ad aggiorna movimenti
          </button>

          <button type="submit" className="btn-primario" disabled={saving}>
            {saving ? "Salvataggio..." : "Salva movimento"}
          </button>
        </div>
      </form>
    </div>
  );
}